import copy from '../../copy/en/shell.json' with { type: 'json' };
import {
  ArrowRight,
  LogOut,
  Menu as MenuIcon,
  Moon,
  Palette,
  Search,
  ShieldAlert,
  ShieldCheck,
  ShieldOff,
  Sun,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { roleLabel } from "../../shared/roles.js";
import { useMe } from "../auth/MeProvider.js";
import { useSession } from "../auth/SessionProvider.js";
import { Badge } from "../components/ui/Badge.js";
import { IconButton } from "../components/ui/Button.js";
import {
  Menu,
  MenuContent,
  MenuItem,
  MenuLabel,
  MenuSeparator,
  MenuTrigger,
} from "../components/ui/Menu.js";
import type { AppEnv } from "../lib/config.js";
import type { Me } from "../lib/me.js";
import { useTheme } from "../theme/index.js";

export function environmentBadgeCopy(env: AppEnv | undefined): {
  label: string;
  title: string;
  ariaLabel: string;
} | null {
  if (!env || env === "production") return null;
  if (env === "staging") {
    return {
      label: copy.TopBar.stagingPrivateData,
      title: copy.TopBar.stagingProofOfConceptWithSyntheticFixtures,
      ariaLabel: copy.TopBar.stagingEnvironmentContainsSyntheticFixturesAndPrivate,
    };
  }
  return {
    label: copy.TopBar.local,
    title: copy.TopBar.localDevelopmentEnvironment,
    ariaLabel: copy.TopBar.localEnvironment,
  };
}

export function TopBar({ onOpenNav }: { onOpenNav: () => void }) {
  const { config } = useSession();
  const { hasCap } = useMe();
  const environment = environmentBadgeCopy(config.appEnv);

  return (
    <header className="topbar">
      <IconButton
        icon={MenuIcon}
        label={copy.TopBar.openNavigation}
        className="topbar__menu-btn"
        onClick={onOpenNav}
      />
      {hasCap("practices:view") ? <PracticeSearch /> : null}
      <div className="topbar__spacer" />
      <div className="topbar__right">
        {environment ? (
          <span
            className="env-badge"
            title={environment.title}
            aria-label={environment.ariaLabel}
          >
            {environment.label}
          </span>
        ) : null}
        <ModeToggle />
        <UserMenu />
      </div>
    </header>
  );
}

export function PracticeSearch() {
  const navigate = useNavigate();
  const location = useLocation();
  const name =
    location.pathname === "/search"
      ? (new URLSearchParams(location.search).get("q") ?? "")
      : "";
  const [query, setQuery] = useState(name);
  useEffect(() => setQuery(name), [name, location.key]);
  return (
    <form
      className="topbar__search"
      role="search"
      aria-label={copy.TopBar.portalRecords}
      onSubmit={(event) => {
        event.preventDefault();
        const params = new URLSearchParams();
        if (query.trim()) params.set("q", query.trim());
        navigate(`/search${params.size ? `?${params}` : ""}`);
      }}
    >
      <Search className="topbar__search-icon" aria-hidden />
      <input
        className="input"
        type="search"
        placeholder={copy.TopBar.searchPortal}
        aria-label={copy.TopBar.searchPortalRecords}
        value={query}
        onChange={(event) => setQuery(event.target.value)}
      />
      <button
        type="submit"
        className="topbar__search-submit"
        aria-label={copy.TopBar.submitPortalSearch}
      >
        <ArrowRight width={16} height={16} aria-hidden />
      </button>
    </form>
  );
}

export function ModeToggle() {
  const { mode, toggleMode } = useTheme();
  const Icon = mode === "dark" ? Sun : Moon;
  return (
    <IconButton
      icon={Icon}
      label={mode === "dark" ? copy.TopBar.switchToLightMode : copy.TopBar.switchToDarkMode}
      onClick={toggleMode}
    />
  );
}

export function mfaBadge(me: Me | null): {
  label: string;
  tone: "success" | "warning" | "danger" | "neutral";
  icon: typeof ShieldCheck;
} {
  const mfa = me?.mfa;
  if (!mfa)
    return { label: copy.TopBar.mfaNotEnforced, tone: "neutral", icon: ShieldOff };
  if (mfa.aal === "aal2")
    return { label: copy.TopBar.mfaVerified, tone: "success", icon: ShieldCheck };
  if (!mfa.required)
    return { label: copy.TopBar.mfaOptional, tone: "neutral", icon: ShieldOff };
  if (mfa.enrolled)
    return {
      label: copy.TopBar.mfaChallengePending,
      tone: "warning",
      icon: ShieldAlert,
    };
  return {
    label: mfa.enforced ? "MFA required" : "MFA enrollment pending",
    tone: mfa.enforced ? "danger" : "warning",
    icon: ShieldAlert,
  };
}

function initials(email: string | null): string {
  if (!email) return "?";
  const local = email.split("@")[0] ?? "";
  const parts = local.split(/[._-]+/).filter(Boolean);
  const s = (
    parts.length >= 2 ? parts[0][0] + parts[1][0] : local.slice(0, 2)
  ).toUpperCase();
  return s || "?";
}

export function UserMenu() {
  const { me, hasCap } = useMe();
  const { signOut, viewAs } = useSession();
  const navigate = useNavigate();
  const badge = mfaBadge(me);
  const email = me?.email ?? copy.TopBar.signedIn;

  return (
    <Menu>
      <MenuTrigger asChild>
        <button type="button" className="user-btn" aria-label={copy.TopBar.accountMenu}>
          <span className="avatar" aria-hidden>
            {initials(me?.email ?? null)}
          </span>
          <span className="user-btn__email">{email}</span>
        </button>
      </MenuTrigger>
      <MenuContent>
        <div className="user-menu__header">
          <div className="user-menu__email">{email}</div>
          <div className="user-menu__meta">
            {me?.role ? <Badge tone="info">{roleLabel(me.role)}</Badge> : null}
            <Badge tone={badge.tone} title={badge.label}>
              <badge.icon width={12} height={12} aria-hidden />
              {badge.label}
            </Badge>
          </div>
        </div>
        <MenuSeparator />
        {!viewAs ? <><MenuLabel>{copy.TopBar.security}</MenuLabel>
        <MenuItem onSelect={() => navigate("/mfa")}>
          <ShieldCheck />{copy.TopBar.twoFactorAuthentication}</MenuItem></> : null}
        {hasCap("users:manage") ? (
          <MenuItem onSelect={() => navigate("/admin/styleguide")}>
            <Palette />{copy.TopBar.styleGuide}</MenuItem>
        ) : null}
        <MenuSeparator />
        <MenuItem
          danger
          onSelect={() => void signOut(copy.TopBar.youHaveBeenSignedOut)}
        >
          <LogOut />{copy.TopBar.signOut}</MenuItem>
      </MenuContent>
    </Menu>
  );
}
